import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useAddAgentKnowledgeBase,
  useAgentKnowledgeBases,
  useKnowledgeBases,
} from '../../hooks/useKnowledge';
import type { KnowledgeBase } from '../../types';

/**
 * Attach a knowledge base to an agent (tasks 573/574).
 *
 * The mirror of `AgentMcpConnectionSelector`: pick one of the account's bases,
 * already-attached ones filtered out. Attaching adds standing knowledge — the
 * agent searches every base attached to it, so this is a scope decision rather
 * than a preference.
 */
interface AgentKnowledgeBaseSelectorProps {
  agentId: string;
  isOpen: boolean;
  onClose: () => void;
}

const BASES_PAGE_SIZE = 100;

export function AgentKnowledgeBaseSelector({
  agentId,
  isOpen,
  onClose,
}: AgentKnowledgeBaseSelectorProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<KnowledgeBase | null>(null);

  const { data, isLoading, error } = useKnowledgeBases(1, BASES_PAGE_SIZE, {
    search: search || undefined,
  });
  const { data: attached } = useAgentKnowledgeBases(agentId);
  const addAttachment = useAddAgentKnowledgeBase();

  const allBases = data?.data ?? [];
  const attachedIds = useMemo(
    () => new Set((attached ?? []).map((attachment) => attachment.knowledgeBaseId)),
    [attached]
  );
  const available = useMemo(
    () => allBases.filter((base) => !attachedIds.has(base.id)),
    [allBases, attachedIds]
  );

  const handleClose = () => {
    if (addAttachment.isPending) return;
    setSearch('');
    setSelected(null);
    onClose();
  };

  const handleAdd = async () => {
    if (!selected) return;
    try {
      await addAttachment.mutateAsync({ agentId, knowledgeBaseId: selected.id });
      setSearch('');
      setSelected(null);
      onClose();
    } catch (addError) {
      console.error('Failed to attach the knowledge base:', addError);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-knowledge-base-selector-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3
            id="agent-knowledge-base-selector-title"
            className="text-lg font-medium text-gray-900 dark:text-white"
          >
            Attach Knowledge Base
          </h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={addAttachment.isPending}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search knowledge bases..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Loading knowledge bases...
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 dark:text-red-400">
                Error loading knowledge bases. Please try again.
              </p>
            </div>
          ) : available.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {search
                  ? `No available knowledge bases found matching "${search}"`
                  : allBases.length === 0
                    ? 'No knowledge bases available'
                    : 'All knowledge bases are already attached to this agent'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {available.map((base) => (
                <button
                  key={base.id}
                  type="button"
                  onClick={() => setSelected(base)}
                  data-testid={`agent-knowledge-base-option-${base.id}`}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selected?.id === base.id
                      ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white mb-1">{base.name}</div>
                  {base.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {base.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={addAttachment.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!selected || addAttachment.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addAttachment.isPending ? 'Attaching...' : 'Attach Knowledge Base'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgentKnowledgeBaseSelector;
